/**
 * Bottom Navigation Bar
 * Mobile-only bottom navigation with iOS-first design
 * 
 * @author Varun Pandey
 */

import React, { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, FolderPlus, Wallet, Star, ChevronLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '../../utils/cn';
import { EXPRESS_BOOKING_GIF_URL } from '../../constants/expressBookingAssets';
import { isSingerOrLiveBandCategory } from '../../data/membershipPlansData';
import { useArtistDetailsStore } from '../../stores/artistDetailsStore';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode | null;
  path: string;
}

const navItems: NavItem[] = [
  {
    id: 'expressBooking',
    label: '',
    icon: null,
    path: '/express-booking',
  },
  {
    id: 'home',
    label: 'Home',
    icon: <Home className="h-5 w-5" />,
    path: '/user-home',
  },
  {
    id: 'jobs',
    label: 'Jobs',
    icon: <FolderPlus className="h-5 w-5" />,
    path: '/jobs',
  },
  {
    id: 'wallet',
    label: 'Wallet',
    icon: <Wallet className="h-5 w-5" />,
    path: '/wallet',
  },
  {
    id: 'membership',
    label: 'Level UP',
    icon: <Star className="h-5 w-5" />,
    path: '/membership',
  },
];

export const BottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const artistDetails = useArtistDetailsStore((s) => s.data);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const [indicatorStyle, setIndicatorStyle] = React.useState<{ left: number; width: number } | null>(null);
  const showExpressBookingNav = isSingerOrLiveBandCategory(artistDetails?.artist_category?.name);
  const expressBookingPath = artistDetails?.eb_tnc ? '/express-booking' : '/express-booking-consent-form';
  const visibleNavItems = React.useMemo(
    () =>
      navItems
        .filter((item) => (item.id === 'expressBooking' ? showExpressBookingNav : true))
        .map((item) => {
          if (item.id === 'expressBooking') {
            return { ...item, path: expressBookingPath };
          }
          return item;
        }),
    [expressBookingPath, showExpressBookingNav]
  );

  // Determine active nav based on current path
  const getActiveNav = (): string => {
    const path = location.pathname;
    if (path === '/express-booking' || path === '/express-booking-consent-form') return 'expressBooking';
    if (path === '/user-home' || path === '/home') return 'home';
    if (path === '/jobs') return 'jobs';
    if (path === '/wallet') return 'wallet';
    if (path === '/membership') return 'membership';
    return '';
  };

  const activeNav = getActiveNav();

  useEffect(() => {
    const activeButton = buttonRefs.current[activeNav];
    if (activeButton) {
      const rect = activeButton.getBoundingClientRect();
      const navRect = activeButton.closest('nav')?.getBoundingClientRect();
      if (navRect) {
        const left = rect.left - navRect.left + rect.width / 2 - 24; // 24px = half of 48px width
        setIndicatorStyle({ left, width: 48 });
      }
    }
  }, [activeNav]);

  // Initial position calculation
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const activeButton = buttonRefs.current[activeNav];
      if (activeButton) {
        const rect = activeButton.getBoundingClientRect();
        const navRect = activeButton.closest('nav')?.getBoundingClientRect();
        if (navRect) {
          const left = rect.left - navRect.left + rect.width / 2 - 24;
          setIndicatorStyle({ left, width: 48 });
        }
      }
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  const handleNavClick = (item: NavItem) => {
    navigate(item.path);
  };

  return (
    <nav className="lg:hidden w-full z-50 border-t border-[#3A3A3C] bottom-nav-safe relative overflow-hidden bg-transparent touch-none select-none">
      {/* Solid top bar */}
      <div className="absolute top-0 left-0 right-0 h-[3.75rem] bg-[#1C1C1E]" />

      {/* Blurred, low-opacity faded tail (extra height below the bar) */}
      <div
        className="absolute bottom-0 left-0 right-0 h-14 pointer-events-none bg-gradient-to-b from-[#1C1C1E] via-[#151518] to-[#0B0B0D]"
      />

      <div className="relative flex items-center justify-between h-[3.75rem] px-2">
        {/* Active Indicator - Single element that moves */}
        {indicatorStyle && (
          <motion.div
            layoutId="activeIndicator"
            className="absolute top-0 h-0.5 bg-[#F33363] rounded-full"
            style={{
              left: `${indicatorStyle.left}px`,
              width: `${indicatorStyle.width}px`,
            }}
            initial={false}
            transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30,
            }}
          />
        )}

        {/* Back Button - No indicator */}
        <motion.button
          onClick={() => navigate(-1)}
          className={cn(
            // Back button is intentionally narrower than other tabs.
            'flex items-center justify-center w-10 flex-none h-full relative transition-colors',
            'active:bg-[#2C2C2E] rounded-lg',
            'text-[#8E8E93]'
          )}
          whileTap={{ scale: 0.95 }}
        >
          {/* Icon */}
          <div className="transition-transform">
            <ChevronLeft className="h-5 w-5" />
          </div>
        </motion.button>

        {visibleNavItems.map((item) => {
          const isActive = activeNav === item.id;

          return (
            <motion.button
              key={item.id}
              ref={(el) => {
                buttonRefs.current[item.id] = el;
              }}
              onClick={() => handleNavClick(item)}
              className={cn(
                'flex flex-col items-center justify-center flex-1 h-full min-h-0 relative transition-colors',
                item.id === 'expressBooking' ? 'gap-0' : 'gap-1',
                'active:bg-[#2C2C2E] rounded-lg',
                isActive ? 'text-white' : 'text-[#8E8E93]'
              )}
              whileTap={{ scale: 0.95 }}
            >
              {/* Icon */}
              <div
                className={cn(
                  'flex items-center justify-center transition-transform',
                  item.id === 'expressBooking'
                    ? 'h-10 w-[4.5rem] max-w-full shrink-0'
                    : 'h-5 w-5',
                  isActive && item.id !== 'expressBooking' && 'scale-110'
                )}
              >
                {item.id === 'expressBooking' ? (
                  <img
                    src={EXPRESS_BOOKING_GIF_URL}
                    alt="Express Booking"
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  item.icon
                )}
              </div>

              {/* Label (skip if empty to support icon-only tabs) */}
              {item.label ? (
                <span
                  className={cn(
                    'text-[12px] font-medium transition-colors uppercase tracking-wide',
                    isActive ? 'text-white' : 'text-[#8E8E93]'
                  )}
                >
                  {item.label}
                </span>
              ) : null}
            </motion.button>
          );
        })}
      </div>
    </nav>
  );
};
